<template>
  <v-dialog class="add-integration-details-popup"
            :value="visiblePopup"
            :max-width='750'
            @click:outside='onCloseClickButton'
            @keydown.esc="onCloseClickButton">
    <v-card class="add-integration-details-popup__wrapper">
      <div class="add-integration-details-popup__wrapper__header">
        <div class="add-integration-details-popup__wrapper__title">{{$t("add.integration.popup.title")}}</div>
        <span class='add-integration-details-popup__wrapper__close__close_btn' @click='onCloseClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class="add-integration-details-popup__wrapper__integration-details">
        <div class='add-integration-details-popup__wrapper__integration-details__icon' v-html="integrationIcon"></div>
        <div>
          <div class="add-integration-details-popup__wrapper__integration-details__subtitle">{{getDisplayName}}</div>
          <div class="add-integration-details-popup__wrapper__integration-details__description">{{getDescription}}</div>
        </div>
      </div>
      <v-divider />
      <div class='section-title'> {{ $t('integration.configuration.title') }} </div>
      <v-form ref='form' v-model='valid' v-if='integrationData.metaData && integrationData.metaData.config'>
        <div class="form-elements">
          <template v-for="(element, index) in elements">
            <component
              class='element'
              outlined
              dense
              :style='`width: ${element.width};`'
              v-model='integrationData.metaData.config[element.id]'
              :is="nameToComponentMap[element.type]"
              v-bind='{...element.props, rules: getRulesPerElement(element.rules) || []}'
              :key="index"/>
          </template>
        </div>
        <div class='conditions' v-if='conditionsAvailableForIntegration'>
          <v-divider />
          <div class='section-title'> {{ $t('integration.conditions.title') }} </div>
          <div class='conditions-summary' v-if='conditionsSummary.length > 0'>
            <div class='conditions-summary__title'>
              {{ $t('conditions.summary.title') }}
            </div>
            <v-divider vertical/>
            <div class='conditions-summary__content'>
              {{ conditionsSummary }}
            </div>
          </div>
          <div class="form-conditions">
            <v-menu
              :close-on-content-click='true'
              rounded
              offset-x
              :nudge-width="25"
              bottom
              right
            >
              <template v-slot:activator="{ attrs, on }">
                <div class="add-new-condition-section" v-bind="attrs" v-on="on">
                  <div class="add-new-condition-header">
                    <label  class="add-new-condition-add-button">
                      <span class="plus">+</span>{{ $t('add.new.condition.button') }}
                    </label>
                  </div>
                </div>
              </template>
              <v-list
                class="profile-menu"
              >
                <v-list-item
                  v-for="(item, index) in addConditionMenuOptions"
                  :key="index"
                  @click='menuOptionSelected(item, [])'
                >
                  <v-list-item-content>
                    <v-list-item-title v-text="item" />
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            <div class='condition-row' v-for='(condition, index) in conditions' :key='index'>
              <condition-row
                :conditions-list='conditions'
                :condition='condition'
                :condition-options='conditionOptions'
                :has-next-relation='getValueOfHasNextRelation(condition, index)'
                :has-group-relation='getValueOfHasGroupRelation(condition, index)'
                @deleteCurrentItem='deleteCurrentItem'
                @addCondition='addCondition'
                @addConditionGroup='addConditionGroup'
              />
            </div>
          </div>
        </div>
        <div class='add-integration-details-popup__wrapper__footer'>
          <div class="buttons">
            <div class='test-section'>
              <v-btn class="btn-tertiary test" :disabled="!valid" :loading="integrationTestingLoading" @click="testIntegration">
                {{ $t('test') }}
              </v-btn>
              <div class='test-indication' v-if='testIndication !== null'>
                <div class='test-successful' v-if='testIndication'>
                  <span class='label'> {{ $t('test.passed.successfully.label') }} </span>
                  <span class='verified material-icons-outlined'> verified </span>
                </div>
                <div class='test-failed' v-else-if='!testIndication'>
                  <span class='label'> {{ $t('test.failed.label') }} </span>
                  <span class='block material-icons-outlined'> block </span>
                </div>
              </div>
            </div>
            <div>
              <v-btn class="btn-tertiary cancel" @click="onCloseClickButton">
                {{ $t('cancel') }}
              </v-btn>
              <v-btn class="btn-primary save" :disabled="!valid" @click="save">
                {{ $t('save') }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { elementNameToVuetifyComponent } from '@/constants/integrations';
import { CLEAR_INTEGRATION_DETAILS, TEST_INTEGRATION } from '@/store/integration/actionTypes';
import { rules } from '@/utils/validationUtils';
import ConditionRow from '@/components/common/ConditionRow.vue';
import { buildSummary } from '@/utils/conditionUtils';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'AddIntegrationDetailsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: { ConditionRow },
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  methods: {
    getValueOfHasGroupRelation(condition, index) {
      return condition.children !== undefined && index < this.conditions.length - 1;
    },
    getValueOfHasNextRelation(condition, index) {
      if (condition.children !== undefined) {
        if (condition.children.length > 0) {
          return true;
        }
        return false;
      }
      return index < this.conditions.length - 1;
    },
    menuOptionSelected(item, conditionIds) {
      if (item === this.$t('add.condition.group.option')) {
        this.addConditionGroup(conditionIds);
      } else {
        this.addCondition(conditionIds);
      }
    },
    addCondition(conditionIds) {
      this.idTracker += 1;
      const newCondition = {
        id: this.idTracker,
        parentId: null,
        condition: '',
        operator: '',
        value: '',
        relation: '',
      };
      if (conditionIds.length) {
        let tempConditionIdx = this.conditions.findIndex(condition => condition.id === conditionIds[0]);
        let tempCondition = this.conditions[tempConditionIdx];
        for (let i = 1; i < conditionIds.length; i += 1) {
          tempConditionIdx = tempCondition.children.findIndex(child => child.id === conditionIds[i]);
          tempCondition = tempCondition.children[tempConditionIdx];
        }
        newCondition.parentId = tempCondition.id;
        tempCondition.children.push(newCondition);
      } else {
        this.conditions.push(newCondition);
      }
    },
    addConditionGroup(conditionIds) {
      this.idTracker += 1;
      const newCondition = {
        id: this.idTracker,
        parentId: null,
        condition: '',
        operator: '',
        value: '',
        children: [],
        relation: '',
        groupRelation: '',
      };
      if (conditionIds.length) {
        let tempConditionIdx = this.conditions.findIndex(condition => condition.id === conditionIds[0]);
        let tempCondition = this.conditions[tempConditionIdx];
        for (let i = 1; i < conditionIds.length; i += 1) {
          tempConditionIdx = tempCondition.children.findIndex(child => child.id === conditionIds[i]);
          tempCondition = tempCondition.children[tempConditionIdx];
        }
        newCondition.parentId = tempCondition.id;
        tempCondition.children.push(newCondition);
      } else {
        this.conditions.push(newCondition);
      }
    },
    deleteCurrentItem(conditionIds) {
      if (conditionIds.length > 1) {
        let tempConditionIdx = this.conditions.findIndex(condition => condition.id === conditionIds[0]);
        let tempCondition = this.conditions[tempConditionIdx];
        for (let i = 1; i < conditionIds.length - 1; i += 1) {
          tempConditionIdx = tempCondition.children.findIndex(child => child.id === conditionIds[i]);
          tempCondition = tempCondition.children[tempConditionIdx];
        }
        const conditionIdxToDelete = tempCondition.children.findIndex(child => child.id === conditionIds[conditionIds.length - 1].id);
        tempCondition.children.splice(conditionIdxToDelete, 1);
      } else {
        const conditionIdxToDelete = this.conditions.findIndex(condition => condition.id === conditionIds[0]);
        this.conditions.splice(conditionIdxToDelete);
      }
    },
    getRulesPerElement(elementRules) {
      const currentElementRules = [];
      const elementRulesKeys = Object.keys(elementRules).filter(key => elementRules[key]);
      elementRulesKeys.forEach(ruleKey => {
        if (Object.keys(this.rules).includes(ruleKey)) {
          currentElementRules.push(this.rules[ruleKey]);
        }
      });
      return currentElementRules;
    },
    onCloseClickButton() {
      this.$emit('closed');
    },
    async testIntegration() {
      try {
        await this.$store.dispatch(TEST_INTEGRATION, this.integrationData);
        this.testIndication = true;
      } catch (e) {
        this.testIndication = false;
      }
    },
    async save() {
      this.integrationData.metaData.lastConditionId = this.idTracker;
      this.integrationData.metaData.conditions = [...this.conditions];
      this.$emit('save', this.integrationData);
    },
    init() {
      this.conditions = [];
      this.idTracker = 0;
      this.testIndication = null;
      this.integrationData.name = this.integrationDetails.name;
      this.integrationData.category = this.integrationDetails.category;
      this.integrationData.conditions_available = this.integrationDetails.conditions_available;
      this.integrationData.metaData = {
        author: this.integrationDetails.author,
        description: this.integrationDetails.description,
        shortDescription: this.integrationDetails.short_description,
        displayName: this.integrationDetails.displayName,
        icon: this.integrationDetails.icon,
        elements: this.integrationDetails.elements,
        config: {},
      };
      this.elements.forEach(element => {
        this.integrationData.metaData.config[element.id] = element.default;
      });
    },
  },
  watch: {
    visiblePopup() {
      if (this.visiblePopup) {
        this.init();
      } else {
        this.$store.dispatch(CLEAR_INTEGRATION_DETAILS);
      }
    },
  },
  data() {
    return {
      valid: false,
      integrationData: {},
      testIndication: null,
      conditions: [],
      idTracker: 0,
    };
  },
  computed: {
    ...mapGetters(['integrationDetails', 'integrationTestingLoading']),
    conditionsSummary() {
      return buildSummary(this.conditions);
    },
    relationOptions() {
      return [this.$t('and.relation.option'), this.$t('or.relation.option')];
    },
    addConditionMenuOptions() {
      return [
        this.$t('add.condition.group.option'),
        this.$t('add.single.condition.option'),
      ];
    },
    conditionOptions() {
      return this.integrationDetails.conditionOptions || [];
    },
    rules() {
      return rules;
    },
    nameToComponentMap() {
      return elementNameToVuetifyComponent;
    },
    elements() {
      return this.integrationDetails.elements?.map(item => item.element) || [];
    },
    conditionsAvailableForIntegration() {
      return this.integrationDetails.conditions_available === undefined ? true : this.integrationDetails.conditions_available;
    },
    getDisplayName() {
      return this.integrationDetails.displayName;
    },
    getDescription() {
      return this.integrationDetails.description;
    },
    integrationIcon() {
      return this.integrationDetails.icon || '';
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.section-title {
  margin-top: rem(30);
  color: var(--text-primary);
  font-size: $font-size-base;
  font-weight: 600;
  letter-spacing: 1.12px;
  line-height: $space-4;
  text-transform: uppercase;
  padding-bottom: rem(10);
}
.conditions-summary {
  display: flex;
  margin-top: rem(25);
  margin-bottom: rem(30);
  align-items: center;
  padding: rem(15);
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-md;
  &__title {
    width: rem(100);
    color: var(--text-secondary);
    font-size: $font-size-sm;
    font-weight: 600;
    letter-spacing: 1.12px;
    line-height: $space-4;
    text-transform: uppercase;
    padding-bottom: rem(10);
  }
  &__content {
    margin-left: $space-5;
    flex: 1;
    color: var(--text-primary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
    margin-bottom: rem(10);
  }
}
.add-integration-details-popup {
  &__wrapper {
    max-width: rem(750);
    padding: $space-4 $space-6;

    &__title {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: rem(24);
      font-weight: bold;
      line-height: rem(24);
      letter-spacing: 0;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__close__close_btn {
      cursor: pointer;
      font-size: $font-size-md;

      &:hover {
        color: var(--brand-primary);
      }
    }
    &__integration-details {
      display: flex;
      flex-direction: row;
      margin-top: rem(15);
      margin-bottom: rem(15);
      &__subtitle {
        color: var(--text-primary);
        font-family: $font-family-medium;
        font-size: $font-size-md;
      }

      &__description {
        color: var(--text-secondary);
        font-family: $font-family-regular;
        font-size: $font-size-base;
        margin-bottom: rem(10);
      }
      &__icon{
        display: flex;
        width: rem(48);
        height: rem(48);
        margin-right: rem(15);
      }
    }
    &__footer {
      display: flex;
      flex-direction: column;

      .buttons {
        margin-top: $space-5;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        .save{
          margin-left: rem(10);
        }
        .cancel{
          box-shadow: $shadow-sm;
        }
        .test-section{
          display: flex;
          flex-direction: row;
          align-items: center;
          .test-indication {
            margin-left: rem(10);
            .test-failed {
              display: flex;
              align-items: center;
              .label {
                font-family: $font-family-regular;
                font-size: $font-size-base;
              }

              .block {
                margin-left: $space-1-5;
                &.material-icons-outlined {
                  font-size: $font-size-xl;
                  color: var(--severity-critical);
                }
              }
            }
            .test-successful {
              display: flex;
              align-items: center;
              .label {
                font-family: $font-family-regular;
                font-size: $font-size-base;
                letter-spacing: 1.1px;
              }
              .verified {
                z-index: 5;
                margin-left: $space-1-5;
                &.material-icons-outlined {
                  font-size: rem(18);
                  color: var(--severity-low);
                }
              }
            }
          }
        }
      }
    }
    .form-conditions {
      display: flex;
      flex-direction: column;
      margin-bottom: $space-10;
      .condition-row {
        margin-top: $space-4;
      }
      .add-new-condition-section {
        display: flex;
        align-items: center;
        max-width: fit-content;
        .add-new-condition-header {
          margin-top: $space-3;
        }
        .add-new-condition-add-button {
          color: var(--brand-primary);
          border: $border-width-thin solid var(--brand-primary);
          border-radius: $radius-md;
          text-transform: uppercase;
          font-size: $font-size-base;
          font-weight: 500;
          letter-spacing: 1.07px;
          line-height: $space-4;
          text-align: center;
          margin-right: $space-3;
          cursor: pointer;
          padding: $space-1-5;
          .plus {
            font-size: rem(18);
          }
          &:hover {
            cursor: pointer;
            background-color: var(--bg-tertiary);
          }
          &:active {
            background-color: var(--bg-tertiary);
          }
        }
      }
    }
    .form-elements {
      display: flex;
      flex-direction: column;
      margin-bottom: rem(30);
      .element {
        margin-top: rem(30);
      }
    }
  }
}
</style>
